import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from 'react-helmet';
import { useParams } from "react-router-dom";
import axios from 'axios';
import Swal from 'sweetalert2';
import DOMPurify from 'isomorphic-dompurify';
import Header from "../Header";
import Footer from "../Footer";
import AppContext from '../AppContext';
import './Career.css';

const getJobId = (job, index) => String(job?._id || job?.id || index);
const slugify = (value) => String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
const getJobSlug = (job, index) => {
    const titleSlug = slugify(job?.job || job?.title || 'career-opening');
    return `${titleSlug}-${getJobId(job, index)}`;
};
const getJobRouteId = (slugOrId) => {
    const value = String(slugOrId || '');
    const idMatch = value.match(/([a-f0-9]{24}|\d+)$/i);
    return idMatch ? idMatch[1] : value;
};

const findJobByRouteParam = (jobs, slugOrId) => {
    if (!Array.isArray(jobs)) {
        return null;
    }

    const routeId = getJobRouteId(slugOrId);
    return jobs.find((job, index) => (
        getJobId(job, index) === routeId || getJobSlug(job, index) === String(slugOrId)
    )) || null;
};

const readStoredJob = (slugOrId) => {
    try {
        const storedJob = localStorage.getItem("Job");
        const parsedJob = storedJob ? JSON.parse(storedJob) : null;

        if (!parsedJob) {
            return null;
        }

        if (!slugOrId) {
            return parsedJob;
        }

        const routeId = getJobRouteId(slugOrId);
        return getJobId(parsedJob, 0) === routeId || getJobSlug(parsedJob, 0) === String(slugOrId)
            ? parsedJob
            : null;
    } catch (error) {
        return null;
    }
};

const CareerDetail = () => {
    const { slug, id } = useParams();
    const routeParam = slug || id;
    const context = useContext(AppContext);
    const [jobs, setJobs] = useState(Array.isArray(context?.apiCareerData) ? context.apiCareerData : []);
    const [job, setJob] = useState(() => findJobByRouteParam(jobs, routeParam) || readStoredJob(routeParam));
    const [loading, setLoading] = useState(!job && !jobs.length);
    const [error, setError] = useState('');
    const [resumeFile, setResumeFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const resumeInputRef = useRef(null);

    useEffect(() => {
        if (jobs.length) {
            setJob(findJobByRouteParam(jobs, routeParam) || readStoredJob(routeParam));
            setLoading(false);
            return;
        }

        axios.get("/careers")
            .then((response) => {
                const nextJobs = Array.isArray(response.data) ? response.data : [];
                setJobs(nextJobs);
                setJob(findJobByRouteParam(nextJobs, routeParam) || readStoredJob(routeParam));
                setLoading(false);
            })
            .catch((requestError) => {
                console.error("Career Detail API Error:", requestError);
                setError("We could not load this opening right now. Please return to careers and try again.");
                setLoading(false);
            });
    }, [routeParam, jobs]);

    const sanitizedDescription = useMemo(() => {
        if (!job?.description) {
            return "";
        }

        return DOMPurify.sanitize(job.description);
    }, [job]);

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        setResumeFile(file || null);
    };

    const handleResumeUpload = (event) => {
        event.preventDefault();

        if (!job) {
            return;
        }

        if (!resumeFile) {
            Swal.fire({
                icon: 'error',
                title: 'Resume required',
                text: 'Please select your resume before applying.',
            });
            return;
        }

        if (resumeFile.type !== 'application/pdf' || resumeFile.size > 5 * 1024 * 1024) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid resume',
                text: 'Please upload a valid PDF file up to 5MB.',
            });
            return;
        }

        const formData = new FormData();
        formData.append('job_type', job.job || job.title || 'Career Opening');
        formData.append('resume', resumeFile);

        setUploading(true);

        axios.post('/resumeUpload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
            .then((response) => {
                if (!response.data?.error) {
                    setResumeFile(null);
                    if (resumeInputRef.current) {
                        resumeInputRef.current.value = '';
                    }
                    Swal.fire(
                        'Thank you!',
                        'We have received your resume. Our team will contact you soon.',
                        'success'
                    );
                    return;
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Upload failed',
                    text: 'Something went wrong. Please upload a valid PDF file up to 5MB.',
                });
            })
            .catch(() => {
                Swal.fire({
                    icon: 'error',
                    title: 'Upload failed',
                    text: 'Something went wrong. Please try again.',
                });
            })
            .finally(() => {
                setUploading(false);
            });
    };

    const myStyleone = {
        background: "#1f1f22",
        width: "100%",
        zIndex: 2,
    };

    const title = job?.job || job?.title || "Career Opening";
    const location = job?.job_location || job?.location || "Surat, Gujarat";
    const canonicalSlug = job ? getJobSlug(job, jobs.findIndex((item, index) => getJobId(item, index) === getJobId(job, 0))) : routeParam;

    return (
        <>
            <Helmet>
                <title>{title} | Careers at Oengines</title>
                <meta
                    name="description"
                    content={`Apply for ${title} at Oengines. Review role details, location, and submit your resume.`}
                />
                <link rel="canonical" href={`https://www.oengines.com/career/${canonicalSlug}`} />
            </Helmet>
            <Header />
            <div className="breadcumb-wrapper" style={myStyleone}>
                <div className="container">
                    <div className="breadcumb-content">
                        <h1 className="breadcumb-title">{title}</h1>
                        <ul className="breadcumb-menu">
                            <li>{location}</li>
                        </ul>
                    </div>
                </div>
            </div>

            <section className="space">
                <div className="container">
                    {loading && <h4>Loading career details...</h4>}

                    {!loading && error && (
                        <div>
                            <h4 style={{ color: "Red", fontSize: "20px" }}>{error}</h4>
                            <a href="/career" className="as-btn style3">Back to Careers</a>
                        </div>
                    )}

                    {!loading && !error && !job && (
                        <div>
                            <h4 style={{ color: "Red", fontSize: "20px" }}>Opening not found.</h4>
                            <a href="/career" className="as-btn style3">Back to Careers</a>
                        </div>
                    )}

                    {!loading && !error && job && (
                        <div className="row gy-4">
                            <div className="col-lg-8">
                                <div className="title-area mb-35">
                                    <span className="sub-title"><img src="../assets/img/theme-img/title_shape_1.svg" alt="shape" />Role Details</span>
                                    <h2 className="sec-title">{title}</h2>
                                </div>

                                <div
                                    className="career-detail-content"
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizedDescription || "<p>Detailed responsibilities and requirements will be shared during the hiring process.</p>",
                                    }}
                                />
                            </div>

                            <div className="col-lg-4">
                                <div className="experience-box">
                                    <span className="experience-box_num">{job.position > 1 ? `${job.position - 1}+` : (job.position || 1)}</span>
                                    <h6 className="experience-box_title">{title}</h6>
                                    <p className="experience-box_text">Location - {location}</p>
                                    <p className="experience-box_text">Upload your resume as a PDF file up to 5MB.</p>
                                </div>

                                <form onSubmit={handleResumeUpload} className="career-apply-form contact-form ajax-contact mt-4">
                                    <div className="career-upload-field">
                                        <input
                                            ref={resumeInputRef}
                                            id="resume-upload"
                                            type="file"
                                            className="career-upload-input"
                                            accept="application/pdf"
                                            onChange={handleFileChange}
                                        />
                                        <label htmlFor="resume-upload" className="career-upload-label">
                                            <span className="career-upload-icon"><i className="far fa-file-pdf"></i></span>
                                            <span className="career-upload-copy">
                                                <span className="career-upload-title">Upload Resume</span>
                                                <span className="career-upload-name">{resumeFile ? resumeFile.name : 'PDF file up to 5MB'}</span>
                                            </span>
                                            <span className="career-upload-action">Browse</span>
                                        </label>
                                    </div>
                                    <div className="form-btn text-xl-start text-center">
                                        <button type="submit" className="as-btn style3" disabled={uploading}>
                                            {uploading ? 'Uploading...' : 'Apply Now'}
                                            <i className="fa-regular fa-arrow-right ms-2"></i>
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            </section>
            <Footer apiCareerData={jobs} />
        </>
    );
};

export default CareerDetail;
